import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Checkbox,
  FormControlLabel,
  styled
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { format, isValid } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Styled components for the requisition form
const RequisitionContainer = styled(Box)(({ theme }) => ({
  fontFamily: 'Courier New, monospace',
  fontSize: '12px',
  lineHeight: '1.4',
  backgroundColor: 'white',
  padding: '20px',
  maxWidth: '800px',
  margin: '0 auto',
  '& .header': {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '20px',
    borderBottom: '2px solid #000',
    paddingBottom: '10px'
  },
  '& .section': {
    marginBottom: '15px'
  },
  '& .section-title': {
    fontWeight: 'bold',
    borderBottom: '1px solid #000',
    paddingBottom: '5px',
    marginBottom: '10px'
  },
  '& .field-row': {
    display: 'flex',
    marginBottom: '5px'
  },
  '& .field-label': {
    fontWeight: 'bold',
    minWidth: '120px',
    marginRight: '10px'
  },
  '& .field-value': {
    flex: 1
  },
  '& .checkbox-section': {
    marginTop: '10px',
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Courier New, monospace',
      fontSize: '12px',
      lineHeight: '1.4'
    }
  },
  '& .signature-line': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: '40px',
  },
}));

// Helper function to safely format dates
const safeFormatDate = (dateString: string, formatString: string): string => {
  if (!dateString || dateString === 'Not provided' || dateString === 'Invalid date') {
    return 'Not provided';
  }
  
  try {
    const date = new Date(dateString);
    if (!isValid(date)) {
      return 'Not provided';
    }
    return format(date, formatString);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return 'Not provided';
  }
};

interface RequisitionFormProps {
  open: boolean;
  onClose: () => void;
  orderData: {
    patientId: string;
    patientName: string;
    patientDOB: string;
    patientGender?: string;
    patientPhone?: string;
    patientEmail?: string;
    patientMRN?: string;
    patientInsurance?: string;
    patientPolicyNumber?: string;
    orderingProvider: string;
    providerNPI?: string;
    providerClinic?: string;
    providerPhone?: string;
    providerEmail?: string;
    labIntegrationId: string;
    labName: string;
    labPhone?: string;
    labFax?: string;
    labAddress?: string;
    orderDate: string;
    requisitionId: string;
    orderType: string;
    orderCode: string;
    orderDescription: string;
    specimenType?: string;
    fastingRequired?: boolean;
    collectionTime?: string;
    bodySite?: string;
    laterality?: string;
    notes?: string;
    icd10Codes?: string[];
    reasonForTest?: string;
  };
}

export const RequisitionForm: React.FC<RequisitionFormProps> = ({
  open,
  onClose,
  orderData,
}) => {
  const currentDate = format(new Date(), 'yyyy-MM-dd HH:mm');
  // Validate orderData to prevent runtime errors
  const validatedOrderData = {
    patientId: orderData?.patientId || '',
    patientName: orderData?.patientName || 'Unknown Patient',
    patientDOB: orderData?.patientDOB || 'Not provided',
    patientGender: orderData?.patientGender || 'Not specified',
    patientPhone: orderData?.patientPhone || 'Not provided',
    patientEmail: orderData?.patientEmail || 'Not provided',
    patientMRN: orderData?.patientMRN || 'Not assigned',
    patientInsurance: orderData?.patientInsurance || 'Not provided',
    patientPolicyNumber: orderData?.patientPolicyNumber || 'Not provided',
    orderingProvider: orderData?.orderingProvider || 'Not specified',
    providerNPI: orderData?.providerNPI || 'Not provided',
    providerClinic: orderData?.providerClinic || 'Not specified',
    providerPhone: orderData?.providerPhone || 'Not provided',
    providerEmail: orderData?.providerEmail || 'Not provided',
    labIntegrationId: orderData?.labIntegrationId || '',
    labName: orderData?.labName || 'Not specified',
    labPhone: orderData?.labPhone || 'Not provided',
    labFax: orderData?.labFax || 'Not provided',
    labAddress: orderData?.labAddress || 'Not provided',
    orderDate: orderData?.orderDate || new Date().toISOString().split('T')[0],
    requisitionId: orderData?.requisitionId || 'LAB-REQ-0000',
    orderType: orderData?.orderType || '',
    orderCode: orderData?.orderCode || '',
    orderDescription: orderData?.orderDescription || '',
    specimenType: orderData?.specimenType || undefined,
    fastingRequired: orderData?.fastingRequired,
    collectionTime: orderData?.collectionTime || undefined,
    bodySite: orderData?.bodySite || undefined,
    laterality: orderData?.laterality || undefined,
    notes: orderData?.notes || undefined,
    icd10Codes: orderData?.icd10Codes || [],
    reasonForTest: orderData?.reasonForTest || undefined,
  };

  // State for instructions checkboxes
  const [sendEmail, setSendEmail] = useState(true);
  const [faxResults, setFaxResults] = useState(false);
  const [copyToPatient, setCopyToPatient] = useState(false);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      try {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Lab/Imaging Requisition Form</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                line-height: 1.4; 
                margin: 20px; 
              }
              .header { text-align: center; font-weight: bold; font-size: 16px; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
              .section { margin-bottom: 15px; }
              .section-title { font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 5px; margin-bottom: 10px; }
              .field-row { display: flex; margin-bottom: 5px; }
              .field-label { font-weight: bold; min-width: 120px; margin-right: 10px; }
              .field-value { flex: 1; }
              .checkbox-section { margin-top: 10px; }
              .signature-line {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                margin-top: 40px;
              }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div id="requisition-form-content">
              ${document.getElementById('requisition-form-content')?.innerHTML || ''}
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      } catch (error) {
        console.error('Error generating print window:', error);
        alert('Error generating print window. Please try again.');
      }
    }
  };

  const handleGeneratePDF = async () => {
    const element = document.getElementById('requisition-form-content');
    if (!element) return;

    try {
      // Create a clone of the element to avoid affecting the original
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.width = '800px';
      clone.style.backgroundColor = 'white';
      clone.style.padding = '20px';
      document.body.appendChild(clone);

      // Generate canvas from the cloned element
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: clone.scrollHeight
      });

      // Remove the clone
      document.body.removeChild(clone);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `Lab_Requisition_${validatedOrderData.patientName.replace(/\s+/g, '_')}_${validatedOrderData.orderDate}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Lab/Imaging Requisition Form</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleGeneratePDF}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <RequisitionContainer id="requisition-form-content">
          <div className="header">
            LAB/IMAGING REQUISITION FORM
          </div>

          <div className="section">
            <div className="field-row">
              <span className="field-label">Date:</span>
              <span className="field-value">{safeFormatDate(validatedOrderData.orderDate, 'yyyy-MM-dd')}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Requisition ID:</span>
              <span className="field-value">{validatedOrderData.requisitionId}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Patient Information:</div>
            <div className="field-row">
              <span className="field-label">Name:</span>
              <span className="field-value">{validatedOrderData.patientName}</span>
            </div>
            <div className="field-row">
              <span className="field-label">DOB:</span>
              <span className="field-value">{safeFormatDate(validatedOrderData.patientDOB, 'MM/dd/yyyy')}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Gender:</span>
              <span className="field-value">{validatedOrderData.patientGender}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{validatedOrderData.patientPhone}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Email:</span>
              <span className="field-value">{validatedOrderData.patientEmail}</span>
            </div>
            <div className="field-row">
              <span className="field-label">MRN:</span>
              <span className="field-value">{validatedOrderData.patientMRN}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Insurance:</span>
              <span className="field-value">{validatedOrderData.patientInsurance}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Policy #:</span>
              <span className="field-value">{validatedOrderData.patientPolicyNumber}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Ordering Provider:</div>
            <div className="field-row">
              <span className="field-label">Name:</span>
              <span className="field-value">{validatedOrderData.orderingProvider}</span>
            </div>
            <div className="field-row">
              <span className="field-label">NPI:</span>
              <span className="field-value">{validatedOrderData.providerNPI}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Clinic:</span>
              <span className="field-value">{validatedOrderData.providerClinic}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{validatedOrderData.providerPhone}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Direct Email:</span>
              <span className="field-value">{validatedOrderData.providerEmail}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Facility Performing Test:</div>
            <div className="field-row">
              <span className="field-label">Name:</span>
              <span className="field-value">{validatedOrderData.labName}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{validatedOrderData.labPhone}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Fax:</span>
              <span className="field-value">{validatedOrderData.labFax}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Address:</span>
              <span className="field-value">{validatedOrderData.labAddress}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Requested Tests:</div>
            <div className="field-row">
              <span className="checkbox">☑</span>
              <span className="field-value">{validatedOrderData.orderCode} — {validatedOrderData.orderDescription}</span>
            </div>
            {validatedOrderData.specimenType && (
              <div className="field-row">
                <span className="field-label">Specimen Type:</span>
                <span className="field-value">{validatedOrderData.specimenType}</span>
              </div>
            )}
            {validatedOrderData.fastingRequired !== undefined && (
              <div className="field-row">
                <span className="field-label">Fasting Required:</span>
                <span className="field-value">
                  ☑ {validatedOrderData.fastingRequired ? 'Yes' : 'No'}   
                  ☐ {validatedOrderData.fastingRequired ? 'No' : 'Yes'}
                </span>
              </div>
            )}
            {validatedOrderData.collectionTime && (
              <div className="field-row">
                <span className="field-label">Collection Time:</span>
                <span className="field-value">{validatedOrderData.collectionTime}</span>
              </div>
            )}
            {validatedOrderData.bodySite && (
              <div className="field-row">
                <span className="field-label">Body Site:</span>
                <span className="field-value">{validatedOrderData.bodySite}</span>
              </div>
            )}
            {validatedOrderData.laterality && (
              <div className="field-row">
                <span className="field-label">Laterality:</span>
                <span className="field-value">{validatedOrderData.laterality}</span>
              </div>
            )}
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Clinical Information:</div>
            {validatedOrderData.icd10Codes && validatedOrderData.icd10Codes.length > 0 && (
              <>
                <div className="field-row">
                  <span className="field-label">ICD-10 Codes:</span>
                </div>
                <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                  {validatedOrderData.icd10Codes.join(', ')}
                </div>
              </>
            )}
            {validatedOrderData.reasonForTest && (
              <>
                <div className="field-row">
                  <span className="field-label">Reason for Test:</span>
                </div>
                <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                  {validatedOrderData.reasonForTest}
                </div>
              </>
            )}
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Instructions for Lab:</div>
            <Box className="checkbox-section">
              <FormControlLabel
                control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />}
                label="Send results via Direct Email to ordering provider"
              />
              <FormControlLabel
                control={<Checkbox checked={faxResults} onChange={e => setFaxResults(e.target.checked)} />}
                label="Fax results to clinic"
              />
              <FormControlLabel
                control={<Checkbox checked={copyToPatient} onChange={e => setCopyToPatient(e.target.checked)} />}
                label="Provide copy to patient"
              />
            </Box>
          </div>

          <Divider />
          <div className="section">
            <div className="signature-line">
              <Typography variant="body2" sx={{ borderTop: '1px solid black', paddingTop: '4px', width: '60%' }}>
                Signature of Ordering Provider
              </Typography>
              <Typography variant="body2">
                Date/Time: {currentDate}
              </Typography>
            </div>
          </div>
        </RequisitionContainer>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}; 